import {
  format,
  parseISO,
  differenceInCalendarDays,
  differenceInBusinessDays,
  eachDayOfInterval,
  isWeekend,
  addDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isSameDay,
  isWithinInterval,
} from 'date-fns';

// ─── Date formatting ─────────────────────────────────────────────────────────

/** Format ISO string to display date. */
export function formatDate(iso: string, fmt = 'MMM d, yyyy'): string {
  return format(parseISO(iso), fmt);
}

/** Format ISO string to short day label (e.g. "Mon 5"). */
export function formatDayLabel(iso: string): string {
  return format(parseISO(iso), 'EEE d');
}

// ─── Date calculations ───────────────────────────────────────────────────────

/** Get the number of calendar days between two ISO date strings (inclusive). */
export function daySpan(startIso: string, endIso: string): number {
  return differenceInCalendarDays(parseISO(endIso), parseISO(startIso)) + 1;
}

/** Get the number of business days between two ISO date strings (inclusive). */
export function businessDaySpan(startIso: string, endIso: string): number {
  const start = parseISO(startIso);
  const end = parseISO(endIso);
  return differenceInBusinessDays(end, start) + (isWeekend(end) ? 0 : 1);
}

/** Get all dates in a range as ISO strings. */
export function datesInRange(startIso: string, endIso: string): string[] {
  const start = parseISO(startIso);
  const end = parseISO(endIso);
  return eachDayOfInterval({ start, end }).map((d) =>
    format(d, 'yyyy-MM-dd'),
  );
}

/** Shift an ISO date string by a number of days. */
export function shiftDate(iso: string, days: number): string {
  return format(addDays(parseISO(iso), days), 'yyyy-MM-dd');
}

/** Check whether a given ISO date falls on a weekend. */
export function isWeekendDate(iso: string): boolean {
  return isWeekend(parseISO(iso));
}

/** Check if two ISO date strings represent the same calendar day. */
export function isSameDate(a: string, b: string): boolean {
  return isSameDay(parseISO(a), parseISO(b));
}

/** Check whether an ISO date is within a range (inclusive). */
export function isDateInRange(
  dateIso: string,
  startIso: string,
  endIso: string,
): boolean {
  return isWithinInterval(parseISO(dateIso), {
    start: parseISO(startIso),
    end: parseISO(endIso),
  });
}

// ─── Range helpers ───────────────────────────────────────────────────────────

/** Get the ISO start/end for the week containing a given date. */
export function weekRange(iso: string, weekStartsOn: 0 | 1 = 1): { start: string; end: string } {
  const date = parseISO(iso);
  return {
    start: format(startOfWeek(date, { weekStartsOn }), 'yyyy-MM-dd'),
    end: format(endOfWeek(date, { weekStartsOn }), 'yyyy-MM-dd'),
  };
}

/** Get the ISO start/end for the month containing a given date. */
export function monthRange(iso: string): { start: string; end: string } {
  const date = parseISO(iso);
  return {
    start: format(startOfMonth(date), 'yyyy-MM-dd'),
    end: format(endOfMonth(date), 'yyyy-MM-dd'),
  };
}

/** Get today as an ISO date string (yyyy-MM-dd). */
export function todayISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

// ─── Grid position helpers ───────────────────────────────────────────────────

/**
 * Calculate the left offset (in columns) for an item on the grid.
 * Returns a 0-based column index relative to the visible range start.
 */
export function gridColumnStart(
  itemStartIso: string,
  rangeStartIso: string,
): number {
  return differenceInCalendarDays(
    parseISO(itemStartIso),
    parseISO(rangeStartIso),
  );
}

/**
 * Calculate the column span for an item on the grid.
 */
export function gridColumnSpan(
  itemStartIso: string,
  itemEndIso: string,
): number {
  return daySpan(itemStartIso, itemEndIso);
}
